/**
 * Soft≠Live Fleet Device Overview — Live right-now + Live operator cmds.
 * Soft `rssi + " dBm"` / Soft `vbat + " V"` invent when null stays deleted.
 * Page99 residual: liquid shell · Re-read · Live CTAs.
 */
import { useState } from "react";
import { html, Card, KV, Alert, Ico } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { presenceDiagnoseView } from "./fleet-live.js";
import { openLiveSession } from "./udock-api.js";
import { confirmDeviceCommand } from "./device-command-gate.js";
import { operatorSay } from "./operator-say.js";

/** Live rssi from detail / last_status — never invent 0 or -127. */
export function rssiOf(detail, fallback) {
  const st = detail && detail.last_status_json && typeof detail.last_status_json === "object"
    ? detail.last_status_json : {};
  const wifi = st.wifi && typeof st.wifi === "object" ? st.wifi : {};
  for (const cand of [st.rssi, wifi.rssi, detail && detail.rssi, fallback]) {
    if (cand == null) continue;
    const n = Number(cand);
    if (Number.isFinite(n) && n !== -127) return n;
  }
  return null;
}

/** Live battery voltage — absent stays null. */
export function vbatOf(detail, fallback) {
  const st = detail && detail.last_status_json && typeof detail.last_status_json === "object"
    ? detail.last_status_json : {};
  for (const cand of [st.vbat, detail && detail.vbat, fallback]) {
    if (cand == null) continue;
    const n = Number(cand);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

function fmtAgeS(n) {
  if (n == null || !Number.isFinite(Number(n)) || Number(n) < 0) return "—";
  const s = Math.floor(Number(n));
  if (s < 60) return s + " s ago";
  if (s < 3600) return Math.floor(s / 60) + " m ago";
  return Math.floor(s / 3600) + " h ago";
}

function llFmt(ll) {
  if (!Array.isArray(ll) || ll.length < 2) return "—";
  const lng = Number(ll[0]);
  const lat = Number(ll[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return "—";
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return "—";
  return lng.toFixed(5) + ", " + lat.toFixed(5);
}

/**
 * @param {{
 *   deviceId: string,
 *   device: object,
 *   offline?: boolean,
 *   commandAllowed?: boolean,
 *   toast: function,
 * }} props
 */
export function DeviceOverviewLive({ deviceId, device, offline, commandAllowed, toast }) {
  const did = String(deviceId || "").trim();
  const d = device || {};
  const act = useAction();
  const [liveBusy, setLiveBusy] = useState(false);
  const cmdOk = commandAllowed === true;
  const detailLive = useApi(did ? path("get__devices_p", { pathParams: [did] }) : "");
  const diagLive = useApi(
    did ? path("get__devices_p_presence-diagnose", { pathParams: [did] }) : "",
  );
  const detail = detailLive.data && typeof detailLive.data === "object" ? detailLive.data : null;
  const view = presenceDiagnoseView(diagLive.data);

  const rssi = rssiOf(detail, d.rssi);
  const vbat = vbatOf(detail, d.vbat);
  const fw = detail
    ? String(detail.fw || "").trim()
    : String(d.fw || "").trim();
  const isOnline = detail && detail.is_online != null
    ? !!detail.is_online
    : !offline;
  const ageS = view && view.ages
    ? (view.ages.signal != null ? view.ages.signal
      : (view.ages.heartbeat != null ? view.ages.heartbeat : view.ages.updated))
    : (Number.isFinite(Number(d.age)) && Number(d.age) >= 0 ? Number(d.age) : null);
  const position = detail && detail.map_lat != null && detail.map_lng != null
    ? [Number(detail.map_lng), Number(detail.map_lat)]
    : d.ll;
  const kind = String(d.kind || "node");
  const err = detailLive.error || diagLive.error;

  const reload = () => {
    detailLive.reload && detailLive.reload();
    diagLive.reload && diagLive.reload();
    toast && toast("Overview re-read", "go");
  };

  const target = [{ id: did, label: d.label || did }];

  const sendCmd = async (cmd, params, okMsg) => {
    if (!did) { toast("No device id", "warn"); return; }
    if (!cmdOk) { toast("Commands closed for this seat", "warn"); return; }
    const ok = await confirmDeviceCommand({
      kind: cmd,
      targets: target,
      detail: "This reaches the selected machine. Cancel leaves hardware untouched.",
    });
    if (!ok) return;
    const out = await act.run(
      path("post__devices_p_commands", { pathParams: [did] }),
      { method: "POST", body: { cmd, params: params || {} } },
    );
    if (out) toast(okMsg || ("Command sent · " + (d.label || did)), "go");
    else toast(act.error || ("Command refused · " + cmd), "bad");
  };

  const soundSiren = async () => {
    if (!did) { toast("No device id", "warn"); return; }
    if (!cmdOk) { toast("Commands closed for this seat", "warn"); return; }
    const ok = await confirmDeviceCommand({
      kind: "alert_on",
      targets: target,
      detail: "Sounds the siren for about 8 seconds on this machine.",
      confirmLabel: "Sound siren",
    });
    if (!ok) return;
    const out = await act.run(
      path("post__devices_p_alert_on", { pathParams: [did], query: { duration_ms: 8000 } }),
      { method: "POST" },
    );
    if (out) toast("Siren armed · 8 s · " + (d.label || did), "warn");
    else toast(act.error || "Siren command refused", "bad");
  };

  const silenceSiren = async () => {
    if (!did) { toast("No device id", "warn"); return; }
    if (!cmdOk) { toast("Commands closed for this seat", "warn"); return; }
    const ok = await confirmDeviceCommand({
      kind: "alert_off",
      targets: target,
      detail: "Stops the siren on this machine only.",
      confirmLabel: "Silence siren",
    });
    if (!ok) return;
    const out = await act.run(
      path("post__devices_p_alert_off", { pathParams: [did] }),
      { method: "POST" },
    );
    if (out) toast("Siren stopped · " + (d.label || did), "go");
    else toast(act.error || "Silence refused", "bad");
  };

  const openVideo = async () => {
    if (!cmdOk) { toast("Commands closed for this seat", "warn"); return; }
    const reason = window.prompt(
      "Why open live video? (min 8 characters)",
      "Operator review on machine overview",
    );
    if (reason == null) return;
    setLiveBusy(true);
    try {
      const out = await openLiveSession(did, { operator_reason: reason, ttl_s: 300 });
      if (!out || !out.ok) {
        toast((out && out.error) || "Live session refused", "bad");
        return;
      }
      const item = (out.data && out.data.item) || out.data || {};
      toast(
        "Live session · " + (item.state || "requested")
          + (item.media_played ? " · host media_played" : " · lease only")
          + " · Soft≠ remux until board-loop w1=remux",
        "go",
      );
    } finally {
      setLiveBusy(false);
    }
  };

  return html`<div className="cmc-ov-live cmc-ov-live--liquid" data-cmc-page="page99-device-live" data-cmc-safe="device-overview-gated">
    <${Alert} tone="info" title="Live overview"
      say="Readings come from the machine’s last Live frame. Physical commands ask for confirmation before they reach hardware. Empty stays empty — nothing Soft-filled."/>
    ${act.envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(act.envelope) || "—"} tone=${act.envelope.ok ? "hi" : "warn"}/>`
      : null}

    <div className="btn-row cmc-ov-live__acts" style=${{ marginBottom:12 }}>
      <button className="btn btn--ghost btn--sm" type="button"
        disabled=${!did || detailLive.loading || diagLive.loading}
        onClick=${reload}>
        <${Ico} d="refresh" w=${13}/>Refresh</button>
    </div>

    ${err && !detail
      ? html`<${Alert} tone="warn" title="Live overview partial"
          say=${String(err)}/>`
      : null}

    <div className="grid g2">
      <${Card} title="Right now" meta=${detailLive.loading && !detail ? "reading…" : "Live detail"}>
        <${KV} k="Answered"
          v=${isOnline
            ? (ageS != null ? fmtAgeS(ageS) : "online · age not in frame")
            : (view && view.suspect ? ("offline · " + view.suspect) : "not in the last sweep")}
          tone=${isOnline ? "" : "bad"}/>
        <${KV} k="Signal"
          v=${rssi == null ? "—" : (rssi + " dBm")}
          tone=${rssi == null ? "dim" : (rssi < -72 ? "warn" : "")}/>
        <${KV} k="Battery"
          v=${vbat == null ? "—" : (vbat + " V")}
          tone=${vbat == null ? "dim" : (vbat < 3.8 ? "warn" : "")}/>
        <${KV} k="Firmware" v=${fw || "—"} tone=${fw ? "" : "dim"}/>
        <${KV} k="Position" v=${llFmt(position)} tone=${position ? "" : "dim"}/>
        ${view && view.reason ? html`
          <${KV} k="Signal path" v=${operatorSay(view.reason, "Signal path")}/>` : null}
        <p className="note">Every value is the last Live frame. Empty stays empty — nothing Soft-filled from a brochure.</p>
      </${Card}>

      <${Card} title="What it can do" meta="Confirm before send">
        <div className="dacts" data-cmc-cmd-ok=${cmdOk ? "1" : "0"}>
          ${kind === "siren" ? html`
            <button className="dact" type="button" disabled=${act.busy || !cmdOk}
              onClick=${soundSiren}><b>Sound it</b><span>8 second burst</span></button>
            <button className="dact" type="button" disabled=${act.busy || !cmdOk}
              onClick=${silenceSiren}><b>Silence it</b><span>Stop this siren</span></button>`
           : kind === "camera" ? html`
            <button className="dact" type="button" disabled=${act.busy || !cmdOk}
              onClick=${()=>sendCmd("snapshot", {}, "Frame request · " + (d.label || did))}><b>Take a frame</b><span>Request a snapshot</span></button>
            <button className="dact" type="button" disabled=${act.busy || liveBusy || !cmdOk}
              onClick=${openVideo}><b>Open live video</b><span>Five minute session</span></button>`
           : kind === "contact" ? html`
            <button className="dact" type="button" disabled=${act.busy || !cmdOk}
              onClick=${()=>sendCmd("hold", {}, "Hold · " + (d.label || did))}><b>Hold it closed</b><span>Keep contact closed</span></button>
            <button className="dact" type="button" disabled=${act.busy || !cmdOk}
              onClick=${()=>sendCmd("release", {}, "Release · " + (d.label || did))}><b>Release</b><span>Clear hold</span></button>`
           : html`
            <button className="dact" type="button" disabled=${act.busy || !cmdOk}
              onClick=${()=>sendCmd("ping", {}, "Check-in · " + (d.label || did))}><b>Ask it to check in</b><span>Presence ping</span></button>`}
          <button className="dact" type="button" disabled=${act.busy || !cmdOk}
            onClick=${()=>sendCmd("reboot", {}, "Reboot queued · " + (d.label || did))}>
            <b>Reboot</b><span>Restart this machine</span></button>
        </div>
        <p className="note">Refused commands toast failure — never invent success. Capability depends on role and device policy.</p>
      </${Card}>
    </div>
  </div>`;
}
